import * as React from 'react';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import EqualizerIcon from '@mui/icons-material/Equalizer'
import AirplaneTicketIcon from '@mui/icons-material/AirplaneTicket'
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import LogoutIcon from '@mui/icons-material/Logout'
import PersonIcon from '@mui/icons-material/Person';
import { Badge, Tooltip, useTheme } from '@mui/material';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AuthContext from '../../context/auth';
import { useNavigate } from 'react-router-dom';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsIcon from '@mui/icons-material/Payments';
import PeopleIcon from '@mui/icons-material/People';
import LayoutBackgroundImage from './layout-background.png'
import useAxios from 'axios-hooks'
import moment from 'moment';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

export function Layout({children}: {children: React.ReactNode}) {
  const [open, setOpen] = React.useState(false);
  const { me, signOut } = React.useContext(AuthContext)
  const navigate = useNavigate()
  const theme = useTheme()

  const [payments, getPayments] = useAxios('/payment', {
    manual: true,
    autoCancel: false
  })

  const [orders, getOrders] = useAxios('/order', {
    manual: true,
    autoCancel: false
  })

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const onLogoutClick = React.useCallback(() => {
    signOut(() => {
      window.location.href = '/'
    })
  }, [signOut, navigate])
  
  const activeListItemStyles = React.useCallback((url: string) => {
    let basePathName = `/${window.location.pathname.split('/')[1]}`
    
    return url.includes(basePathName) ? {
      color: theme.palette.primary.main
    }: {}
  }, [theme])

  React.useEffect(() => {
    if (me?.role === 'ADMIN' && !payments.loading) {
      getPayments()
      getOrders()
    }
  }, [me?.role])
  
  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            Bem-vindo(a) à Next Gate, {me?.name}!
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <div style={{
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <img alt="logo" src="/logo.png" height={50} />
          </div>
          {open && (<IconButton onClick={handleDrawerClose}>
            {!open ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>)}
        </DrawerHeader>
        <Divider />
        <List>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                ...activeListItemStyles('/home')
              }}
              href={'/home'}
            >
            <Tooltip title='Home'>
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <EqualizerIcon style={activeListItemStyles('/home')}/>
              </ListItemIcon>
              </Tooltip>
              <ListItemText primary='Home' sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                ...activeListItemStyles('/minhas-ofertas')
              }}
              href={'/minhas-ofertas'}
            >
            <Tooltip title='Minhas Ofertas'>
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                {
                  me?.bids.filter(item => item.status === 'ACCEPTED').length ? (
                    <Badge color="success" badgeContent={me?.bids.filter(item => item.status === 'ACCEPTED').length}>
                      <ChecklistRtlIcon style={activeListItemStyles('/minhas-ofertas')} />
                    </Badge>
                  ) : (
                    <ChecklistRtlIcon style={activeListItemStyles('/minhas-ofertas')} />
                  )
                }
              </ListItemIcon>
              </Tooltip>
              <ListItemText primary='Minhas Ofertas' sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                ...activeListItemStyles('/meus-pagamentos')
              }}
              href={'/meus-pagamentos'}
            >
              <Tooltip title='Meus Pagamentos'>
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <PaymentsIcon style={activeListItemStyles('/meus-pagamentos')} />
              </ListItemIcon>
              </Tooltip>
              <ListItemText primary='Meus Pagamentos' sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                ...activeListItemStyles('/minhas-contas')
              }}
              href={'/minhas-contas'}
            >

            <Tooltip title='Minhas Contas'>
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <PeopleIcon style={activeListItemStyles('/minhas-contas')} />
              </ListItemIcon>
              </Tooltip>
              <ListItemText primary='Minhas Contas' sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                ...activeListItemStyles('/meu-perfil')
              }}
              href={'/meu-perfil'}
            >

            <Tooltip title='Meu Perfil'>
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                {
                  !me?.pix_key ? (
                    <Badge color="error" badgeContent={"!"}>
                      <PersonIcon style={activeListItemStyles('/meu-perfil')} />
                    </Badge>
                  ) : (
                    <PersonIcon style={activeListItemStyles('/meu-perfil')} />
                  )
                }
              </ListItemIcon>
              </Tooltip>
              <ListItemText primary='Meu Perfil' sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
        </List>
        <Divider />
        {
          me?.role === 'ADMIN' && (
            <React.Fragment>
              <List>
                <ListItem disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                      ...activeListItemStyles('/usuarios')
                    }}
                    href={'/usuarios'}
                  >
                  <Tooltip title='Usuários'>
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      <PeopleIcon style={activeListItemStyles('/usuarios')} />
                    </ListItemIcon>
                  </Tooltip>
                    <ListItemText primary='Usuários' sx={{ opacity: open ? 1 : 0 }} />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                      ...activeListItemStyles('/ofertas')
                    }}
                    href={'/ofertas'}
                  >
                  <Tooltip title='Ofertas'>
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      <ChecklistRtlIcon style={activeListItemStyles('/ofertas')} />
                    </ListItemIcon>
                    </Tooltip>
                    <ListItemText primary='Ofertas' sx={{ opacity: open ? 1 : 0 }} />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                      ...activeListItemStyles('/emissoes')
                    }}
                    href={'/emissoes'}
                  >
                  <Tooltip title='Emissões'>
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                     { orders?.data?.find((item: {confirmation: string, created_at: Date}) => item.confirmation === 'PENDING' && moment(item.created_at).isBefore(moment().subtract(48, 'hours'))) ? (
                      <Badge color="error" badgeContent={"!"}>
                      <AirplaneTicketIcon style={activeListItemStyles('/emissoes')} />
                      </Badge>
                    ) : (
                      <AirplaneTicketIcon style={activeListItemStyles('/emissoes')} />
                    )}
                    </ListItemIcon>
                    </Tooltip>
                    <ListItemText primary='Emissões' sx={{ opacity: open ? 1 : 0 }} />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                      ...activeListItemStyles('/pagamentos')
                    }}
                    href={'/pagamentos'}
                  >
                  <Tooltip title='Pagamentos'>
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      { payments?.data?.find((item: {status: string}) => item.status === 'LATE') ? (
                      <Badge color="warning" badgeContent={"!"}>
                        <PaymentsIcon style={activeListItemStyles('/pagamentos')} />
                      </Badge>
                    ) : (
                      <PaymentsIcon style={activeListItemStyles('/pagamentos')} />
                    )}
                    </ListItemIcon>
                    </Tooltip>
                    <ListItemText primary='Pagamentos' sx={{ opacity: open ? 1 : 0 }} />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                      ...activeListItemStyles('/cartoes')
                    }}
                    href={'/cartoes'}
                  >
                  <Tooltip title='Cartões'>
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      <CreditCardIcon style={activeListItemStyles('/cartoes')} />
                    </ListItemIcon>
                  </Tooltip>
                    <ListItemText primary='Cartões' sx={{ opacity: open ? 1 : 0 }} />
                  </ListItemButton>
                </ListItem>
              </List>
              <Divider />
            </React.Fragment>
          )
        }
        <List
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
              }}
              href="https://wa.me/5511918569055?text=Ol%C3%A1%2C+estou+com+um+problema+no+site+e+preciso+de+suporte."
              target="_blank"
            >

            <Tooltip title='Ajuda'>
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <QuestionAnswerIcon />
              </ListItemIcon>
              </Tooltip>
              <ListItemText primary='Ajuda' sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
              }}
              onClick={onLogoutClick}
            >
            <Tooltip title='Logout'>
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <LogoutIcon />
              </ListItemIcon></Tooltip>
              <ListItemText primary='Logout' sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
          <div style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'end',
            position: 'relative'
          }}>
            <img alt="logo" src="/logo-square.png" height={50} />
          </div>
        </List>
      </Drawer>
      <Box component="main" sx={{ 
        width: '100%',
        backgroundImage: `url(${LayoutBackgroundImage})`, backgroundPosition: 'center', backgroundSize: 'cover', backgroundRepeat: 'no-repeat', 
        overflow: 'hidden'
        }}>
        <DrawerHeader />
        {children}
      </Box>
    </Box>
  );
}
